import { i18n } from "@lingui/core";
import { msg } from '@lingui/macro';
import { useLingui } from "@lingui/react";
import { Button, MenuItem, Snackbar } from "@mui/material";
import { Alert, Box, Grid, TextField, useTheme } from "@mui/material/";
import * as echarts from "echarts";
import html2canvas from 'html2canvas';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { NewApiBaseUrl } from "../../../consts/new-api-base-url";
import { NAV_LAYOUT_MARGIN_START } from "../../../consts/nav-layout-margin";
import {
  getDefaultStatsReportType,
  isNonInputAnalystUser,
} from "../../../pages/Statistics/statistics-report-type";
import { enrichStatsRequestPayload } from "../../../pages/Statistics/statistics-dataset-context";
import { formatStatsApiError } from "../stats-api-errors";
import { StatsDatasetControls } from "../StatsDatasetControls";
import useHttpApiNew from "../../../hooks/useHttpApiNew";
import useLogout from "../../../hooks/useLogout";
import StatisticsFilterPanel from "../StatisticsFilterPanel";
import EChart from "./EChart";
import DashboardSeveritySummary from "./dashboard-severity-summary";
import DashboardSummaryTable from "./dashboard-summary-table";
import {
  createDashboardChartColorState,
  getDashboardColorMapForChartType,
} from "./dashboard-random-palette";
import { statisticsMapGradientColors } from "./statistics-chart-palette";
import {
  buildStatsChartRequestPayload,
  buildStatsRequestPayload,
  chartFieldExistsInStatsConfig,
  extractSeveritySummaryFromStatsResponse,
  extractYearlySummaryFromStatsResponse,
  isChartFieldSensitiveInStatsConfig,
  normalizeSeveritySummary,
  normalizeStatsRowsToChartData,
  resolveStatsChartFieldName,
  normalizeYearlySummaryRow,
  unwrapStatsChartRows } from
"./stats-response-utils";

export {
  BAR_CHART_COLOR,
  DASHBOARD_COLOR_PALETTE,
  getRandomColors } from
"./dashboard-random-palette";

export {
  chartPalette,
  statisticsBarFillColor,
  statisticsChartsPalette,
  statisticsMapGradientColors,
  statisticsPieCategoryFourColors,
  statisticsPieSeverityColors,
  statisticsTreemapColors,
  treemapAreaTypeContrastPalette } from
"./statistics-chart-palette";

const getStatsUrl = new URL(`${NewApiBaseUrl}/Stats`);
const getYearlySummaryUrl = new URL(`${NewApiBaseUrl}/Stats/yearly-summary`);
const reportDocUrl = new URL(`${NewApiBaseUrl}/ReportDoc`);

const REPORT_CHART_API_NUM_TO_FILE_NAME = {
  1: 'crashes_by_severity',
  2: 'crashes_by_year',
  3: 'crashes_by_gender',
  4: 'crashes_by_area_type',
  5: 'crashes_by_administrative_division',
  6: 'crashes_by_user_type',
  7: 'crashes_by_road_function',
  8: 'crashes_by_speed_limit',
  9: 'crashes_by_type',
  10: 'crashes_by_month',
  11: 'crashes_by_day_of_week',
  12: 'crashes_by_age_group',
  13: 'crashes_by_alcohol_involvement',
  14: 'crashes_by_safety_equipment',
  15: 'people_by_injury_severity',
  16: 'crashes_by_weather_conditions',
};

const REPORT_SUMMARY_IMAGE_NAMES = {
  filteredCrashSummary: 'filtered_crash_summary',
  yearlyCrashSummary: 'yearly_crash_summary',
};

/** Minimum layout width so html2canvas captures wide tables at full width. */
const REPORT_SUMMARY_CAPTURE_MIN_WIDTH_PX = 1100;

const CRASH_DATE_FROM_FILTER = 'crash.Crash date.from';
const CRASH_DATE_TO_FILTER = 'crash.Crash date.to';

const formatReportDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseReportFilterDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getReportDateRangeFromFilters = (filterList) => {
  const fromValue = filterList.find((entry) => entry.name === CRASH_DATE_FROM_FILTER)?.value;
  const toValue = filterList.find((entry) => entry.name === CRASH_DATE_TO_FILTER)?.value;
  const fromDate = parseReportFilterDate(fromValue);
  const toDate = parseReportFilterDate(toValue);
  const today = new Date();

  return {
    fromDate: formatReportDate(fromDate ?? today),
    toDate: formatReportDate(toDate ?? fromDate ?? today),
  };
};

const waitForChartPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

const captureElementAsPngBase64 = async (element) => {
  if (!element) return null;

  const previousWidth = element.style.width;
  const previousMinWidth = element.style.minWidth;
  const captureWidth = Math.max(
    element.scrollWidth,
    element.offsetWidth,
    Math.ceil(element.getBoundingClientRect().width),
    REPORT_SUMMARY_CAPTURE_MIN_WIDTH_PX,
  );

  element.style.width = `${captureWidth}px`;
  element.style.minWidth = `${captureWidth}px`;

  try {
    await waitForChartPaint();
    const captureHeight = Math.max(element.scrollHeight, element.offsetHeight);

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      scrollX: 0,
      scrollY: 0,
    });

    return canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
  } finally {
    element.style.width = previousWidth;
    element.style.minWidth = previousMinWidth;
  }
};

const Dashboard = ({ isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { _ } = useLingui();
  const state = location.state;
  const token = state?.token || null;
  const init = useMemo(() => state?.init || {}, [state?.init]);

  const [user, setUser] = useState({});
  const [formModel, setFormModel] = useState({});
  const [formList, setFormList] = useState([]);
  const [charts, setCharts] = useState([]);
  const [filters, setFilters] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [areasGeoJson, setAreasGeoJson] = useState(null);
  const [colSize, setColSize] = useState(2);
  const [errorMessage, setErrorMessage] = useState(null);
  const [yearlySummaryRows, setYearlySummaryRows] = useState([]);
  const [yearlySummaryLoading, setYearlySummaryLoading] = useState(false);
  const [severitySummary, setSeveritySummary] = useState(null);
  const [datasetReportType, setDatasetReportType] = useState(() =>
    getDefaultStatsReportType(init?.user),
  );

  const theme = useTheme();
  const chartRefs = useRef([]);
  const severitySummaryRef = useRef(null);
  const yearlySummaryRef = useRef(null);
  const { request } = useHttpApiNew();

  const init_charts = useMemo(() => {
  const statsReportType = getDefaultStatsReportType(init?.user);
  const regionFieldName = resolveStatsChartFieldName(
    init?.configuration,
    'crash',
    'State',
  );
  return [
  {
    apiNum: 1,
    title: {
      text: _(msg`Crash severity`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'pie',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: 'Crash severity' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Crash severity`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 2,
    title: {
      text: _(msg`Crashes by year`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: 'Crash date' },
      fields: init.configuration,
      filters: [],
      groupBy: 'yyyy'
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Year`),
    yLabel: _(msg`Number of crashes`),
    groupByFormat: 'yyyy',
  },
  {
    apiNum: 3,
    title: {
      text: _(msg`Crashes by gender`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'people', fieldName: 'Gender' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Victim's gender`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 4,
    title: {
      text: _(msg`Crashes by area type`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'treemap',
    payload: {
      chartField: { reportType: statsReportType, target: 'road', fieldName: 'Area type' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Crashes by area type`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 5,
    title: {
      text: _(msg`Crashes by region`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'map',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: regionFieldName },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    mapType: 'areas',
    xLabel: '',
    yLabel: '',
  },
  {
    apiNum: 6,
    title: {
      text: _(msg`Crashes by road user type`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'pie',
    payload: {
      chartField: { reportType: statsReportType, target: 'people', fieldName: 'Road user type' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Crashes by road user type`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 7,
    title: {
      text: _(msg`Crashes by road function`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'pie',
    payload: {
      chartField: { reportType: statsReportType, target: 'road', fieldName: 'Functional class - 1st road' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Road function`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 8,
    title: {
      text: _(msg`Crashes by speed limit`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'road', fieldName: 'Speed limit' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Speed limit`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 9,
    title: {
      text: _(msg`Crashes by type`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: 'Crash type' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Crash type`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 10,
    title: {
      text: _(msg`Crashes by month`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: 'Crash date' },
      fields: init.configuration,
      filters: [],
      groupBy: 'MM'
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Month`),
    yLabel: _(msg`Number of crashes`),
    groupByFormat: 'MM',
  },
  {
    apiNum: 11,
    title: {
      text: _(msg`Crashes by day of week`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: 'Crash date' },
      fields: init.configuration,
      filters: [],
      groupBy: 'dayOfWeek'
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Day of week`),
    yLabel: _(msg`Number of crashes`),
  },
  {
    apiNum: 12,
    title: {
      text: _(msg`Crashes by age group`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'people', fieldName: 'Age group' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Age group`),
    yLabel: _(msg`Number of people`),
  },
  {
    apiNum: 13,
    title: {
      text: _(msg`Alcohol involvement in crashes`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'pie',
    payload: {
      chartField: { reportType: statsReportType, target: 'people', fieldName: 'Alcohol use' },
      fields: init.configuration,
      filters: [],
      countMode: 'distinctCrashes'
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Alcohol use`),
    yLabel: _(msg`Number of people`),
  },
  {
    apiNum: 14,
    title: {
      text: _(msg`Crashes by safety equipment use`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'pie',
    payload: {
      chartField: { reportType: statsReportType, target: 'people', fieldName: 'Safety equipment use' },
      fields: init.configuration,
      filters: [],
      countMode: 'distinctCrashes'
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Safety equipment use`),
    yLabel: _(msg`Number of people`),
  },
  {
    apiNum: 15,
    title: {
      text: _(msg`Injury severity`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'bar',
    payload: {
      chartField: { reportType: statsReportType, target: 'people', fieldName: 'Injury severity' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Injury severity`),
    yLabel: _(msg`Number of people`),
  },
  {
    apiNum: 16,
    title: {
      text: _(msg`Weather conditions`),
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: '#fff'
      }
    },
    type: 'pie',
    payload: {
      chartField: { reportType: statsReportType, target: 'crash', fieldName: 'Weather conditions' },
      fields: init.configuration,
      filters: []
    },
    data: [],
    isLoading: false,
    xLabel: _(msg`Weather conditions`),
    yLabel: _(msg`Number of crashes`),
  }].
  map((chart) => {
    const colorState = createDashboardChartColorState(
      chart.apiNum,
      chart.type,
      statisticsMapGradientColors,
    );
    const chartWithColors = { ...chart, ...colorState };
    const { target, fieldName } = chart.payload.chartField;
    const configuration = init?.configuration;
    if (isChartFieldSensitiveInStatsConfig(configuration, target, fieldName)) {
      return { ...chartWithColors, sensitiveField: true, isLoading: false, data: [] };
    }
    return chartWithColors;
  }).
  filter((chart) => {
    if (chart.sensitiveField) return true
    const { target, fieldName } = chart.payload.chartField
    return chartFieldExistsInStatsConfig(init?.configuration, target, fieldName)
  });
  }, [_, init.configuration, init?.user]);

  useEffect(() => {
    const hasMapChart = init_charts.some((chart) => chart.type === 'map');
    if (!hasMapChart) return;

    fetch('/maps/areas.geojson').
    then((res) => res.json()).
    then((geoJson) => {
      echarts.registerMap('areas', geoJson);
      setAreasGeoJson(geoJson);
      setMapReady(true);
    }).
    catch((err) => {});
  }, [init_charts]);

  useEffect(() => {
    if (!token) return logout();
    if (isMobile) return navigate('/home', { state: { token, init } });
  }, [token, logout, isMobile, navigate, init]);

  useEffect(() => {
    const { configuration, user } = init;
    setUser(user);
    setFormModel(configuration);
    const sortedForms = Object.keys(configuration).
    sort((a, b) => configuration[a].position - configuration[b].position);
    setFormList(sortedForms);
    setCharts(init_charts);
    setDatasetReportType(getDefaultStatsReportType(user));
    setDataLoaded(false);
  }, [init, init_charts]);

  useEffect(() => {
    setDataLoaded(false);
  }, [datasetReportType]);

  const handleDatasetReportTypeChange = (nextReportType) => {
    if (nextReportType === datasetReportType) return;
    setYearlySummaryLoading(true);
    setDataLoaded(false);
    setCharts((prev) => prev.map((chart) => ({ ...chart, isLoading: true })));
    setDatasetReportType(nextReportType);
  };

  useEffect(() => {
    const containsMap = init_charts.some((c) => c.type === 'map');
    if (!formModel || Object.keys(formModel).length === 0 || dataLoaded || init_charts.length === 0) return;
    if (containsMap && !mapReady) return;

    let cancelled = false;

    const fetchAllCharts = async () => {
      setYearlySummaryLoading(true);
      setCharts((prev) => prev.map((chart) => ({ ...chart, isLoading: true })));
      const settled = await Promise.allSettled([
      fetchYearlySummary(),
      ...init_charts.map((chart) =>
        chart.sensitiveField
          ? Promise.resolve({ data: [], severitySummary: null })
          : fetchChartData(chart)
      )]
      );
      if (!cancelled) {
        const [summarySettled, ...chartSettled] = settled;
        const summaryRows =
        summarySettled.status === 'fulfilled' ? summarySettled.value : [];

        const chartResults = chartSettled.map((result) => {
          if (result.status === 'fulfilled') {
            return result.value;
          }
          return { data: [], severitySummary: null };
        });

        setYearlySummaryRows(summaryRows);
        setYearlySummaryLoading(false);
        const nextSeverity = chartResults.
        map((result) => result.severitySummary).
        find(Boolean) ?? null;
        setSeveritySummary(nextSeverity);
        const updatedCharts = init_charts.map((chart, index) => ({
          ...chart,
          isLoading: false,
          data: chartResults[index]?.data || []
        }));
        setCharts(updatedCharts);
        setDataLoaded(true);
      }
    };

    fetchAllCharts();
    return () => {
      cancelled = true;
    };
  }, [formModel, init_charts, dataLoaded, filters, mapReady, datasetReportType]);

  const displayCharts = useMemo(() => {
    const byApiNum = new Map();
    for (const chart of charts) {
      if (!byApiNum.has(chart.apiNum)) {
        byApiNum.set(chart.apiNum, chart);
      }
    }
    return Array.from(byApiNum.values()).sort((a, b) => a.apiNum - b.apiNum);
  }, [charts]);

  const defaultStatsChartField = useMemo(() => ({
    reportType: datasetReportType,
    target: 'crash',
    fieldName: 'Crash date'
  }), [datasetReportType]);

  const statsDatasetContext = useMemo(() => ({
    user: init?.user,
    organizations: init?.organizations ?? [],
    reportType: datasetReportType,
  }), [init?.user, init?.organizations, datasetReportType]);

  const fetchYearlySummary = async () => {
    const filtersPayload = filters.length > 0 ? filters : [];
    const summaryPayload = enrichStatsRequestPayload(
      buildStatsRequestPayload({
        chartField: defaultStatsChartField,
        fields: formModel,
        filters: filtersPayload
      }),
      statsDatasetContext,
    );

    let res = await request(
      getYearlySummaryUrl,
      'POST',
      summaryPayload,
      { Authorization: `Bearer ${token}` }
    );

    if (!res.responseAnyError) {
      const rows = extractYearlySummaryFromStatsResponse(res.responseData);
      if (rows.length > 0) {
        return rows.map(normalizeYearlySummaryRow).sort((a, b) => b.year - a.year);
      }
    }

    const summaryTablePayload = enrichStatsRequestPayload(
      buildStatsRequestPayload({
        chartField: defaultStatsChartField,
        fields: formModel,
        filters: filtersPayload,
        responseFormat: 'summaryTable'
      }),
      statsDatasetContext,
    );
    res = await request(
      getStatsUrl,
      'POST',
      summaryTablePayload,
      { Authorization: `Bearer ${token}` }
    );

    if (res.responseAnyError) {
      return [];
    }

    const rows = extractYearlySummaryFromStatsResponse(res.responseData);
    return rows.map(normalizeYearlySummaryRow).sort((a, b) => b.year - a.year);
  };

  const fetchChartData = async (chart) => {
    const payload = enrichStatsRequestPayload(
      buildStatsChartRequestPayload(chart, filters, formModel),
      statsDatasetContext,
    );
    const res = await request(getStatsUrl, 'POST', payload, { Authorization: `Bearer ${token}` });
    if (res.responseAnyError) {
      setErrorMessage(formatStatsApiError(res, _));
      return { data: [], severitySummary: null };
    }

    const resData = unwrapStatsChartRows(res.responseData);
    const severitySummary = normalizeSeveritySummary(
      extractSeveritySummaryFromStatsResponse(res.responseData)
    );
    const data = normalizeStatsRowsToChartData({
      rawRows: resData,
      formModel,
      chart,
      translate: _
    });

    return {
      data,
      severitySummary
    };
  };

  const handleCharts = (event, apiNum) => {
    const newType = event.target.value;
    setCharts((prevCharts) =>
    prevCharts.map((chart) => {
      if (chart.apiNum !== apiNum) return chart;
      const nextColorMap = getDashboardColorMapForChartType(
        newType,
        statisticsMapGradientColors,
        chart.ordinalColors,
      );
      return { ...chart, type: newType, colorMap: nextColorMap };
    })
    );
  };

  const exportSummarySectionsAsFiles = async () => {
    await waitForChartPaint();

    const files = [];

    const filteredSummaryPng = await captureElementAsPngBase64(severitySummaryRef.current);
    if (filteredSummaryPng) {
      files.push({
        name: REPORT_SUMMARY_IMAGE_NAMES.filteredCrashSummary,
        fileContent: filteredSummaryPng,
      });
    }

    const yearlySummaryPng = await captureElementAsPngBase64(yearlySummaryRef.current);
    if (yearlySummaryPng) {
      files.push({
        name: REPORT_SUMMARY_IMAGE_NAMES.yearlyCrashSummary,
        fileContent: yearlySummaryPng,
      });
    }

    return files;
  };

  const exportChartsAsFiles = async () => {
    await waitForChartPaint();

    const files = [];

    for (let index = 0; index < displayCharts.length; index++) {
      const chart = displayCharts[index];
      const imgData = await chartRefs.current[index]?.getImage();

      if (!imgData || chart.sensitiveField) continue;

      const base64Content = imgData.replace(/^data:image\/png;base64,/, '');

      files.push({
        name: REPORT_CHART_API_NUM_TO_FILE_NAME[chart.apiNum] ?? `chart_${chart.apiNum}`,
        fileContent: base64Content,
      });
    }

    return files;
  };


  const downloadDashboard = async () => {
    /*
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });
     const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
     let yPos = 10;
     const addChartsToPdf = async () => {
        for (let index = 0; index < charts.length; index++) {
            const imgData = chartRefs.current[index]?.getImage();
            if (!imgData) continue;
             await new Promise((resolve) => {
                const img = new Image();
                img.src = imgData;
                img.onload = () => {
                    const imgWidth = img.width;
                    const imgHeight = img.height;
                    const ratio = Math.min( (pageWidth - 20) / imgWidth, 80 / imgHeight );
                     const renderWidth = imgWidth * ratio;
                    const renderHeight = imgHeight * ratio;
                     const x = (pageWidth - renderWidth) / 2;
                    const y = yPos;
                     pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
                     yPos += renderHeight + 10;
                     if (yPos > pageHeight - 30 && index < charts.length - 1) {
                        pdf.addPage();
                        yPos = 10;
                    }
                     resolve();
                };
            });
        }
         pdf.save("dashboard.pdf");
    };
     addChartsToPdf();
    */












    if (!dataLoaded || yearlySummaryLoading) {
      setErrorMessage(_(msg`Please wait for dashboard data to finish loading.`));
      return;
    }

    const summaryFiles = await exportSummarySectionsAsFiles();
    const chartFiles = await exportChartsAsFiles();
    const filesArray = [...summaryFiles, ...chartFiles];
    const { fromDate, toDate } = getReportDateRangeFromFilters(filters);

    const body = {
      images: filesArray,
      fromDate,
      toDate,
      templateName: 'ADaMSPeriodicCrashReport',
    };

    const res = await request(reportDocUrl, 'POST', body, { Authorization: `Bearer ${token}` });

    if (!res.responseAnyError) {
      downloadWordFile(res.responseData.fileContent, "ADaMSPeriodicCrashReport.docx");
    } else {
      setErrorMessage(res.responseMessage);
    }


  };

  const downloadWordFile = (base64Data, fileName) => {
    // ripulisce spazi, newline, ecc.
    const cleaned = base64Data.replace(/\s/g, "");

    // decodifica base64 → binary
    const byteCharacters = atob(cleaned);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // crea un Blob Word (docx è un zip)
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    });

    // crea link temporaneo per scaricare
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      pl={2}
      pr={2}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{
        marginInlineStart: NAV_LAYOUT_MARGIN_START,
        '& .overload-theme--header': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText
        }
      }}>

            {/*
        <Box sx={{
           display: 'flex',
           flexDirection: 'row',
           justifyContent: 'space-between',
           alignItems: 'center',
           width: '100%',
           padding: 1.5
        }}>
           <Typography sx={{fontSize: 18}} variant="h3">
               <Trans>Dashboard</Trans>
           </Typography>
           <Box sx={{alignSelf: 'end'}}>
               <UserControls noElev={true} state={state}/>
           </Box>
        </Box>
        <Divider flexItem/>
        */
      }

            <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={5000} // si chiude dopo 5 secondi
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>

                <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ width: "100%" }}>

                    {errorMessage ? i18n._(errorMessage) : errorMessage}
                </Alert>
            </Snackbar>

            {formList.length > 0 &&
      <StatisticsFilterPanel
        title={_(msg`Dashboard`)}
        newLabel={_(msg`Dashboard`)}
        formList={formList}
        filterDisabled={false}
        headerExtra={
          <StatsDatasetControls
            user={init?.user}
            organizations={init?.organizations ?? []}
            reportType={datasetReportType}
            onReportTypeChange={handleDatasetReportTypeChange}
            showBasicOrganization={false}
            variant="panel"
            lockReportType={isNonInputAnalystUser(init?.user)}
            loading={yearlySummaryLoading}
            isDark={theme.palette.mode === 'dark'}
            theme={theme}
          />
        }
        setDataLoaded={setDataLoaded}
        user={user}
        formModel={formModel}
        isMobile={isMobile}
        setFilters={setFilters} />

      }

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {Number(user?.type) === 1 &&
        <Button
          sx={{
            background: theme.palette.newColors.cyanGradient
          }}
          variant='contained'
          size='small'
          onClick={() => {
            downloadDashboard();
          }}>

                        {_(msg`Download report`)}
                    </Button>
        }
                <TextField size={'small'} sx={{ minWidth: 160 }} select label={_(msg`Chart per row`)} value={colSize}
        onChange={(e) => setColSize(Number(e.target.value))}>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4</MenuItem>
                </TextField>
            </Box>

            <Box ref={severitySummaryRef}>
              <DashboardSeveritySummary
                summary={severitySummary}
                isLoading={!dataLoaded}
              />
            </Box>

            <Box ref={yearlySummaryRef}>
              <DashboardSummaryTable
                rows={yearlySummaryRows}
                isLoading={yearlySummaryLoading}
              />
            </Box>


            <Grid container sx={{
        transition: 'all 0.5s ease-in-out'
      }}>
                {displayCharts.map((chart, index) => {
          if (chart.type === 'map' && !mapReady) return null;
          return (
            <Grid item xs={12 / colSize} key={chart.apiNum} p={2}>
                            <Box sx={{
                transition: 'all 0.5s ease-in-out'
              }}>
                                <EChart
                  ref={(el) => chartRefs.current[index] = el}
                  areasGeoJson={areasGeoJson}
                  chart={chart}
                  index={chart.apiNum}
                  handleCharts={handleCharts}
                  width="100%" />

                            </Box>
                        </Grid>);

        })}

            </Grid>

        </Box>);

};

export default Dashboard;